/**
 * DLITE-5094: browser WebSockets cannot set an `Authorization` header, so
 * commentary-ws (`WsBearerProtocolFilter`) accepts the access token in the
 * `Sec-WebSocket-Protocol` handshake header instead. Ported from commentary-ui,
 * the only other client of this scheme.
 *
 * - The token travels as `bearer.<base64url(token)>` WITHOUT padding: the
 *   server decodes it and validates the result against the JWT charset.
 * - The client must ALSO offer `bearer.acked`. The server echoes that one back
 *   as the selected subprotocol; Chromium fails the handshake (close 1006) if
 *   the server selects a protocol the client did not offer, and the server
 *   never echoes the token-bearing one.
 */
export const BEARER_SUBPROTOCOL_PREFIX = 'bearer.'
export const BEARER_ACKED_SUBPROTOCOL = 'bearer.acked'

/** base64url (RFC 4648 §5) of the UTF-8 bytes of `s`, without `=` padding. */
export function base64UrlEncode(s: string): string {
  // encodeURIComponent → %XX bytes → a binary string btoa accepts. Avoids
  // TextEncoder, which older jsdom test environments lack.
  const binary = encodeURIComponent(s).replace(
    /%([0-9A-F]{2})/g,
    (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)),
  )
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The token-bearing subprotocol, `bearer.<base64url(token)>`. */
export function buildBearerSubprotocol(token: string): string {
  return `${BEARER_SUBPROTOCOL_PREFIX}${base64UrlEncode(token)}`
}

/** The full protocol list to offer: the token, then `bearer.acked`. */
export function bearerSubprotocols(token: string): string[] {
  return [buildBearerSubprotocol(token), BEARER_ACKED_SUBPROTOCOL]
}
